import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { load } from 'js-yaml';
import { storageGet, storageSet } from '../lib/browserCompat';

type GlossaryTable = Record<string, string>;

interface GlossaryFile {
  defaultLocale: string;
  fallbackLocale?: string;
  supportedLocales?: string[];
  locales: Record<string, GlossaryTable>;
}

interface GlossaryContextType {
  locale: string;
  locales: string[];
  ready: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
  setLocale: (locale: string) => void;
}

const STORAGE_KEY = 'garuda_locale';
const GlossaryContext = createContext<GlossaryContextType | null>(null);

function detectLocale(available: string[], fallback: string): string {
  const stored = storageGet(STORAGE_KEY);
  if (stored && available.includes(stored)) return stored;
  const browserTag = navigator.language.toLowerCase();
  if (available.includes(browserTag)) return browserTag;
  const browser = browserTag.split('-')[0];
  if (available.includes(browser)) return browser;
  return available.includes(fallback) ? fallback : available[0] ?? fallback;
}

function interpolate(value: string, params?: Record<string, string | number>): string {
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? `{${key}}`));
}

export function GlossaryProvider({ children }: { children: ReactNode }) {
  const [file, setFile] = useState<GlossaryFile | null>(null);
  const [locale, setLocaleState] = useState('hi');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/glossary.yaml')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const data = load(text) as GlossaryFile;
        if (cancelled) return;
        setFile(data);
        const available = data.supportedLocales ?? Object.keys(data.locales ?? {});
        const fallback = data.fallbackLocale ?? data.defaultLocale ?? 'hi';
        setLocaleState(detectLocale(available, fallback));
        setReady(true);
      })
      .catch((err) => {
        console.error('Failed to load glossary:', err);
        if (!cancelled) {
          setFile({ defaultLocale: 'hi', fallbackLocale: 'hi', locales: { hi: {} } });
          setLocaleState('hi');
          setReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = ['ar', 'fa', 'he', 'ks', 'sd', 'ur'].includes(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  const value = useMemo<GlossaryContextType>(() => {
    const locales = file ? (file.supportedLocales ?? Object.keys(file.locales ?? {})) : ['hi'];
    const fallback = file?.fallbackLocale ?? file?.defaultLocale ?? 'hi';
    const lookup = (key: string, activeLocale = locale): string => {
      const orderedLocales = [
        activeLocale,
        fallback,
        ...(file ? Object.keys(file.locales ?? {}) : []),
      ].filter((item, index, arr) => Boolean(item) && arr.indexOf(item) === index);

      for (const candidate of orderedLocales) {
        const table = file?.locales?.[candidate] ?? {};
        if (table[key]) return table[key];
      }

      return key;
    };
    return {
      locale,
      locales,
      ready,
      t: (key, params) => interpolate(lookup(key), params),
      setLocale: (nextLocale) => {
        if (!file) return;
        const available = file.supportedLocales ?? Object.keys(file.locales ?? {});
        if (!available.includes(nextLocale)) return;
        storageSet(STORAGE_KEY, nextLocale);
        setLocaleState(nextLocale);
      },
    };
  }, [file, locale, ready]);

  return <GlossaryContext.Provider value={value}>{children}</GlossaryContext.Provider>;
}

export function useGlossary(): GlossaryContextType {
  const context = useContext(GlossaryContext);
  if (!context) {
    return {
      locale: 'hi',
      locales: ['hi'],
      ready: false,
      t: (key: string) => key,
      setLocale: () => undefined,
    };
  }
  return context;
}
